// Shared doctor-eligibility rules for booking, used by the booking calendar and
// the appointment edit/create modal so they always agree.
//
// A doctor has a default `spec` (array of appointmentList TYPE strings) and
// `except` (array of {detail} — appointmentList DETAILs the doctor opts out of).
// A timetable shift may carry `custom_spec` (type strings) / `custom_except`
// (appointmentList id numbers) which OVERRIDE the doctor's defaults for that shift.
//
// The override is ALL-OR-NOTHING and keyed on `custom_spec`: a shift that has
// one replaces BOTH lists. So an override with an EMPTY `custom_except` means
// "this shift refuses nothing", not "fall back to the doctor's refusals" —
// otherwise a shift could never be set to accept everything in its spec, and the
// two columns (which the ตารางเวร drawer always writes together) would disagree
// about which record they came from.
//
// treatment = { type, detail?, id? }: type alone → match by spec only; once a
// detail (a specific appointmentList) is chosen → also rule out by except.

export function buildDocInfo(doctors) {
  const info = {}
  for (const d of (doctors || [])) {
    info[String(d.id)] = {
      code: d.code || [d.name, d.surname].filter(Boolean).join(' ') || `หมอ #${d.id}`,
      img: d.profile_img || '',
      active: d.active,
      spec: new Set((d.spec || []).map(String)),
      exceptDetails: new Set((d.except || []).map((e) => e && (e.detail ?? e)).filter(Boolean).map(String)),
    }
  }
  return info
}

export function shiftEligible(row, treatment, docInfo) {
  if (!treatment) return true
  const info = docInfo[String(row.doctor_id)] || { spec: new Set(), exceptDetails: new Set() }
  const custom = Array.isArray(row.custom_spec) && row.custom_spec.filter(Boolean).length > 0
  const spec = custom ? new Set(row.custom_spec.map(String)) : info.spec
  if (!spec.has(treatment.type)) return false
  if (custom) {
    const ex = (row.custom_except || []).map(String)
    return treatment.id ? !ex.includes(String(treatment.id)) : true
  }
  if (!treatment.detail) return true
  return !info.exceptDetails.has(treatment.detail)
}

export function doctorEligible(docId, treatment, docInfo) {
  if (!treatment) return true
  const info = docInfo[String(docId)]
  if (!info || !info.spec.has(treatment.type)) return false
  if (!treatment.detail) return true
  return !info.exceptDetails.has(treatment.detail)
}

// Doctors who work a given day (from that day's timetable rows) AND are eligible
// for the treatment — honoring per-shift custom_spec/custom_except overrides.
export function eligibleDoctorsFromTimetable(timetableRows, treatment, docInfo) {
  const m = new Map()
  for (const row of (timetableRows || [])) {
    if (row.event_type !== 'inverse-background' || row.block === true) continue
    const did = String(row.doctor_id)
    if (!did || did === '0') continue
    if (!shiftEligible(row, treatment, docInfo)) continue
    if (!m.has(did)) m.set(did, {
      id: did,
      name: row.doctor?.code || docInfo[did]?.code || row.titleDoctorTB || `หมอ #${did}`,
      img: row.doctor?.profile_img || docInfo[did]?.img || '',
    })
  }
  return [...m.values()].sort((x, y) => x.name.localeCompare(y.name, 'th'))
}
